<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'shared/components/Spinner.vue';
import WahaSessionsAPI from 'dashboard/api/wahaSessions';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const inboxName = ref('');
const isCreating = ref(false);
const integrationLoading = ref(true);

const wahaIntegration = computed(() => {
  return store.getters['integrations/getIntegration']('waha', {});
});

// The API never returns the api_key (only visible_properties: base_url), and the
// hook can not be saved without it, so an active hook with a URL is configured
const integrationConfigured = computed(() => {
  const activeHook = wahaIntegration.value?.hooks?.find(hook => hook.status);
  return Boolean(activeHook?.settings?.base_url);
});

const loadIntegration = async () => {
  try {
    await store.dispatch('integrations/get');
  } finally {
    integrationLoading.value = false;
  }
};

const openIntegrationSettings = () => {
  router.push({
    name: 'settings_applications_integration',
    params: {
      accountId: route.params.accountId,
      integration_id: 'waha',
    },
  });
};

const createChannel = async () => {
  if (!inboxName.value.trim()) {
    useAlert(t('INBOX_MGMT.ADD.WAHA.NAME_REQUIRED'));
    return;
  }

  try {
    isCreating.value = true;
    const response = await WahaSessionsAPI.create({
      name: inboxName.value.trim(),
    });

    router.replace({
      name: 'settings_inbox_waha_qr',
      params: {
        accountId: route.params.accountId,
        session_id: response.data.id,
      },
      query: { inbox_id: response.data.inbox?.id },
    });
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('INBOX_MGMT.ADD.WAHA.API.ERROR_MESSAGE')
    );
  } finally {
    isCreating.value = false;
  }
};

onMounted(() => {
  loadIntegration();
});
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WAHA.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WAHA.DESC')"
    />

    <div v-if="integrationLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <div
      v-else-if="!integrationConfigured"
      class="p-5 rounded-xl outline outline-1 outline-n-weak bg-n-alpha-2 max-w-2xl"
    >
      <h3 class="text-base font-medium text-n-slate-12 mb-2">
        {{ $t('INBOX_MGMT.ADD.WAHA.INTEGRATION_NOT_ENABLED_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ $t('INBOX_MGMT.ADD.WAHA.INTEGRATION_NOT_ENABLED_MESSAGE') }}
      </p>
      <NextButton
        type="button"
        variant="outline"
        color="slate"
        @click="openIntegrationSettings"
      >
        {{ $t('INBOX_MGMT.ADD.WAHA.GO_TO_INTEGRATION_SETTINGS') }}
      </NextButton>
    </div>

    <form
      v-else
      class="max-w-3xl flex flex-col gap-5"
      @submit.prevent="createChannel"
    >
      <div class="rounded-2xl outline outline-1 outline-n-weak bg-n-card p-5">
        <div
          class="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)] lg:items-start"
        >
          <div>
            <Input
              v-model="inboxName"
              :label="$t('INBOX_MGMT.ADD.WAHA.INBOX_NAME.LABEL')"
              :placeholder="$t('INBOX_MGMT.ADD.WAHA.INBOX_NAME.PLACEHOLDER')"
            />
          </div>

          <div class="rounded-xl bg-n-alpha-2 p-5">
            <h3 class="text-sm font-medium text-n-slate-12 mb-2">
              {{ $t('INBOX_MGMT.ADD.WAHA.WHAT_IS_WAHA_TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mb-3">
              {{ $t('INBOX_MGMT.ADD.WAHA.WHAT_IS_WAHA_DESCRIPTION') }}
            </p>
            <ul class="list-disc pl-5 text-sm text-n-slate-11 space-y-1">
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.FEATURES.NO_OFFICIAL_API') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.FEATURES.QR_CODE') }}</li>
              <li>{{ $t('INBOX_MGMT.ADD.WAHA.FEATURES.MULTI_DEVICE') }}</li>
            </ul>
          </div>
        </div>

        <div
          class="flex items-center justify-end mt-5 pt-5 border-t border-n-weak"
        >
          <NextButton
            type="submit"
            :is-loading="isCreating"
            :disabled="!inboxName.trim()"
          >
            {{ $t('INBOX_MGMT.ADD.WAHA.CREATE_CHANNEL') }}
          </NextButton>
        </div>
      </div>
    </form>
  </div>
</template>
